import {
    AccountBalance,
    Assessment,
    BugReport,
    Business,
    Code,
    CreditCard,
    Diversity3,
    Email,
    Insights,
    LightbulbOutlined,
    LockOpen,
    MenuBook,
    People,
    PersonOutline,
    Security,
    Send,
    SubdirectoryArrowRight,
    TextSnippet,
    TrendingUp,
} from "@mui/icons-material";
import { lazy } from "react";

import type { AdminRouteDefinition } from "./adminRoutes";

export const LEGACY_ADMIN_ROUTES: AdminRouteDefinition[] = [
    {
        id: "users",
        path: "/users",
        label: "Users",
        icon: <People />,
        navGroup: "users_orgs",
        navOrder: 10,
        Component: lazy(() => import("@/pages/Users")),
    },
    {
        id: "user_details",
        path: "/users/:userId",
        label: "User Details",
        icon: <PersonOutline />,
        navGroup: "users_orgs",
        navOrder: 15,
        navHidden: true,
        Component: lazy(() => import("@/pages/UserDetails")),
    },
    {
        id: "organisations",
        path: "/organisations",
        label: "Organisations",
        icon: <Business />,
        navGroup: "users_orgs",
        navOrder: 20,
        Component: lazy(() => import("@/pages/Organisations")),
    },
    {
        id: "partners",
        path: "/partners",
        label: "Partners",
        icon: <Diversity3 />,
        navGroup: "users_orgs",
        navOrder: 22,
        Component: lazy(() => import("@/pages/Partners")),
    },
    {
        id: "partner_details",
        path: "/partners/:partnerId",
        label: "Partner Details",
        icon: <Diversity3 />,
        navGroup: "users_orgs",
        navOrder: 23,
        navHidden: true,
        Component: lazy(() => import("@/pages/PartnerDetails")),
    },
    {
        id: "organisation_details",
        path: "/organisations/:orgId",
        label: "Organisation Details",
        icon: <Business />,
        navGroup: "users_orgs",
        navOrder: 25,
        navHidden: true,
        Component: lazy(() => import("@/pages/OrganisationDetails")),
    },
    {
        id: "user_access_audit",
        path: "/user-access-audit",
        label: "User Access Audit",
        icon: <Security />,
        navGroup: "users_orgs",
        navOrder: 30,
        Component: lazy(() => import("@/pages/UserAccessAudit")),
    },
    {
        id: "subscription",
        path: "/subscription",
        label: "Subscription",
        icon: <CreditCard />,
        navGroup: "billing",
        navOrder: 10,
        Component: lazy(() => import("@/pages/SubscriptionDashboard")),
    },
    {
        id: "quickbooks_trials",
        path: "/admin/quickbooks-trials",
        label: "QuickBooks Trials",
        icon: <CreditCard />,
        navGroup: "billing",
        navOrder: 15,
        Component: lazy(() => import("@/pages/QuickBooksTrials")),
    },
    {
        id: "email_templates",
        path: "/emails",
        label: "Email Templates",
        icon: <Email />,
        navGroup: "email",
        navOrder: 10,
        Component: lazy(() => import("@/pages/Email")),
    },
    {
        id: "email_sender",
        path: "/email-sender",
        label: "Email Sender",
        icon: <Send />,
        navGroup: "email",
        navOrder: 20,
        Component: lazy(() => import("@/pages/EmailSender")),
    },
    {
        id: "bulk_email",
        path: "/bulk-email",
        label: "Bulk Email",
        icon: <Send />,
        navGroup: "email",
        navOrder: 30,
        Component: lazy(() => import("@/pages/BulkEmail")),
    },
    {
        id: "bulk_email_jobs",
        path: "/bulk-email-jobs",
        label: "Email Jobs",
        icon: <Email />,
        navGroup: "email",
        navOrder: 40,
        Component: lazy(() => import("@/pages/BulkEmailJobs")),
    },
    {
        id: "xero_referral_audit",
        path: "/xero",
        label: "Xero Referral Audit",
        icon: <AccountBalance />,
        navGroup: "integrations",
        navOrder: 10,
        Component: lazy(() => import("@/pages/Xero")),
    },
    {
        id: "spoof",
        path: "/spoof",
        label: "Impersonation",
        icon: <PersonOutline />,
        navGroup: "support",
        navOrder: 30.4,
        requiredPermission: "support.spoof_user",
        Component: lazy(() => import("@/pages/Spoof")),
    },
    {
        id: "advanced_spoof",
        path: "/advanced-spoof",
        label: "Advanced Spoof",
        icon: <Security />,
        navGroup: "support",
        navOrder: 30.5,
        navHidden: true,
        requiredPermission: "support.spoof_user",
        Component: lazy(() => import("@/pages/AdvancedSpoofPage")),
    },
    {
        id: "spoof_sessions_audit",
        path: "/spoof-sessions",
        label: "Spoof sessions",
        icon: <Security />,
        navGroup: "settings",
        navOrder: 35,
        requiredPermission: "support.spoof_user",
        Component: lazy(() => import("@/pages/SpoofSessionsAudit")),
    },
    {
        id: "spoof_launcher",
        path: "/spoof-launcher",
        label: "Spoof Launcher",
        icon: <Security />,
        navGroup: "support",
        navOrder: 30.6,
        navHidden: true,
        requiredPermission: "support.spoof_user",
        Component: lazy(() => import("@/pages/SpoofLauncher")),
    },
    {
        id: "statistics",
        path: "/statistics",
        label: "Statistics",
        icon: <Insights />,
        navGroup: "analytics",
        navOrder: 10,
        Component: lazy(() => import("@/pages/Statistics")),
    },
    {
        id: "admin_metrics",
        path: "/admin-metrics",
        label: "Admin Metrics (Legacy)",
        icon: <Insights />,
        navGroup: "analytics",
        navOrder: 15,
        navHidden: true,
        Component: lazy(() => import("@/pages/AdminMetrics")),
    },
    {
        id: "usage_analytics",
        path: "/usage-analytics",
        label: "Usage Analytics",
        icon: <TrendingUp />,
        navGroup: "analytics",
        navOrder: 30,
        Component: lazy(() => import("@/pages/UsageAnalytics")),
    },
    {
        id: "partner_stats",
        path: "/partner-stats",
        label: "Partner Stats",
        icon: <TrendingUp />,
        navGroup: "analytics",
        navOrder: 40,
        Component: lazy(() => import("@/pages/PartnerStats")),
    },
    {
        id: "system_logs",
        path: "/logs",
        label: "System Logs",
        icon: <Assessment />,
        navGroup: "observability",
        navOrder: 10,
        Component: lazy(() => import("@/pages/Logs")),
    },
    // Bug Tracker (admin -> 2-phase AI -> monday).
    // See docs/features/BUG_TRACKER.md
    {
        id: "bug_tracker",
        path: "/bug-tracker",
        label: "Bug Tracker",
        icon: <BugReport />,
        navGroup: "observability",
        navOrder: 20,
        Component: lazy(() => import("@/pages/BugTracker")),
    },
    {
        id: "bug_tracker_recent",
        path: "/bug-tracker/recent",
        label: "Bug Reports",
        icon: <SubdirectoryArrowRight fontSize='small' />,
        navGroup: "observability",
        navOrder: 22,
        navHidden: true,
        Component: lazy(
            () => import("@/pages/BugTracker/BugTrackerRecentPage"),
        ),
    },
    {
        id: "bug_tracker_details",
        path: "/bug-tracker/:bugReportId",
        label: "Bug Report",
        icon: <BugReport />,
        navGroup: "observability",
        navOrder: 25,
        navHidden: true,
        Component: lazy(() => import("@/pages/BugTracker")),
    },
    {
        id: "feature_requests",
        path: "/feature-requests",
        label: "Feature Requests",
        icon: <LightbulbOutlined />,
        navGroup: "observability",
        navOrder: 20.1,
        Component: lazy(() => import("@/pages/FeatureRequests")),
    },
    {
        id: "feature_requests_recent",
        path: "/feature-requests/recent",
        label: "Feature Requests (Recent)",
        icon: <SubdirectoryArrowRight fontSize='small' />,
        navGroup: "observability",
        navOrder: 20.2,
        navHidden: true,
        Component: lazy(() => import("@/pages/FeatureRequestsRecent")),
    },
    {
        id: "feature_requests_details",
        path: "/feature-requests/:featureRequestId",
        label: "Feature Request",
        icon: <LightbulbOutlined />,
        navGroup: "observability",
        navOrder: 20.3,
        navHidden: true,
        Component: lazy(() => import("@/pages/FeatureRequests")),
    },
    {
        id: "transcript_intake",
        path: "/transcript-intake",
        label: "Transcript Intake",
        icon: <TextSnippet />,
        navGroup: "observability",
        navOrder: 27,
        Component: lazy(() => import("@/pages/TranscriptIntake")),
    },
    {
        id: "demo_access",
        path: "/admin/demo-access",
        label: "Demo Access",
        icon: <LockOpen />,
        navGroup: "admin",
        navOrder: 10,
        Component: lazy(() => import("@/pages/DemoAccess")),
    },
    {
        id: "demo_ops",
        path: "/admin/demo-ops",
        label: "Demo Ops",
        icon: <Insights />,
        navGroup: "admin",
        navOrder: 15,
        Component: lazy(() => import("@/pages/DemoOps")),
    },
    {
        id: "docs_share_links",
        path: "/admin/docs-share-links",
        label: "Docs Share Links",
        icon: <Code />,
        navGroup: "dev_tools",
        navOrder: 10,
        Component: lazy(() => import("@/pages/DocsShareLinks")),
    },
    {
        id: "business_types",
        path: "/admin/business-types",
        label: "Business Types",
        icon: <Business />,
        navGroup: "admin",
        navOrder: 18,
        Component: lazy(() => import("@/pages/BusinessTypes")),
    },
    {
        id: "admin_access_audit",
        path: "/admin-access",
        label: "Admin Access Audit",
        icon: <Security />,
        navGroup: "admin",
        navOrder: 20.9,
        Component: lazy(() => import("@/pages/AdminAccessAudit")),
    },
    {
        id: "guides",
        path: "/admin/guides",
        label: "Guides",
        icon: <MenuBook />,
        navGroup: "admin",
        navOrder: 25,
        Component: lazy(() => import("@/pages/Guides")),
    },
    {
        id: "guide_details",
        path: "/admin/guides/:guideId",
        label: "Guide",
        icon: <MenuBook />,
        navGroup: "admin",
        navOrder: 26,
        navHidden: true,
        Component: lazy(() => import("@/pages/Guides")),
    },
    {
        id: "editable_content_block",
        path: "/EditableContentBlock/:contentId",
        label: "Editable Content Block",
        icon: <Business />,
        navGroup: null,
        navOrder: 0,
        navHidden: true,
        Component: lazy(
            () => import("@/pages/EditableContentBlock/EditableContentBlock"),
        ),
    },
];
